import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { ghs, minutes, toPesewas } from '../lib/money.js';
import { Button, Chip, Field, Banner } from '../components/ui.jsx';
import { Payment } from './Payment.jsx';
import { Receipt } from './Receipt.jsx';

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());

export function SalesPoint({ slug, user, creditsStaff = true, initialCart, onConsumedCart }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [cat, setCat] = useState({ categories: [], services: [] });
  const [staff, setStaff] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [query, setQuery] = useState('');
  const [lines, setLines] = useState([]);
  const [editing, setEditing] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [stage, setStage] = useState('cart');     // cart | payment | done
  const [sale, setSale] = useState(null);
  const [error, setError] = useState(null);
  const [appointmentId, setAppointmentId] = useState(null);
  const [held, setHeld] = useState(false);

  // A check in, or a resumed draft, arrives as a ready made cart.
  useEffect(() => {
    if (!initialCart) return;
    setLines(initialCart.lines.map(l => ({ key: uuid(), discount: 0, qty: 1, ...l })));
    setCustomer(initialCart.customerId ? { id: initialCart.customerId, name: initialCart.customerName } : null);
    setAppointmentId(initialCart.appointmentId ?? null);
    setStage('cart');
    onConsumedCart?.();
  }, [initialCart]);

  useEffect(() => {
    b.catalogue().then(setCat).catch(() => {});
    // No staff list where a line is not credited to anybody. The laundry has employees; it
    // just does not attribute a washed shirt to one of them.
    if (creditsStaff) b.staff().then(d => setStaff(d.staff)).catch(() => {});
  }, [slug, creditsStaff]);

  const services = useMemo(() => {
    let list = cat.services;
    if (activeCat) list = list.filter(s => s.category_id === activeCat);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = cat.services.filter(s => s.name.toLowerCase().includes(q));
    }
    return list;
  }, [cat, activeCat, query]);

  const catById = useMemo(
    () => Object.fromEntries(cat.categories.map(c => [c.id, c])),
    [cat.categories]);

  const subtotal = lines.reduce((a, l) => a + Math.max(0, l.unitPrice * l.qty - l.discount), 0);
  const discountTotal = 0;
  const total = subtotal - discountTotal;
  const unassigned = creditsStaff ? lines.filter(l => !l.staffId).length : 0;

  function addService(s) {
    setLines(ls => [...ls, {
      key: uuid(), serviceId: s.id, name: s.name,
      unitPrice: s.price ?? 0, qty: 1, discount: 0, staffId: null, staffName: null
    }]);
  }

  async function complete(payments) {
    setError(null);
    try {
      const { sale } = await b.createSale({
        uuid: uuid(),
        // Who this screen thinks is selling. The server refuses the sale if its session is
        // somebody else, rather than printing the wrong name on the receipt.
        cashierId: user?.id ?? null,
        customerId: customer?.id ?? null,
        customerName: customer?.name ?? null,
        discountTotal,
        lines: lines.map(l => ({
          serviceId: l.serviceId, qty: l.qty, discount: l.discount,
          unitPrice: l.unitPrice, staffId: l.staffId, outsideSpeciality: Boolean(l.outsideSpeciality)
        })),
        payments
      });
      setSale(sale);
      setStage('done');
    } catch (err) {
      setError(err.data?.error === 'credit_requires_customer'
        ? 'Credit needs a named customer. A walk-in cannot owe you money, so attach a customer first.'
        : err.data?.error === 'cashier_mismatch'
        ? `This till is still signed in to the server as ${err.data.sessionName || 'somebody else'}, `
          + 'so the sale was not saved under the wrong name. Go back, hold the sale, sign out, '
          + 'and sign in again as yourself. Nothing was charged.'
        : 'That sale could not be saved. Nothing was charged.');
    }
  }

  function newSale() {
    setLines([]); setCustomer(null); setSale(null); setStage('cart');
    setError(null); setQuery(''); setAppointmentId(null); setHeld(false);
  }

  async function hold() {
    if (!lines.length) return;
    await b.holdDraft({
      label: customer?.name || 'Walk-in',
      payload: { customerId: customer?.id ?? null, customerName: customer?.name ?? null, appointmentId, lines }
    }).catch(() => {});
    setHeld(true);
    setTimeout(newSale, 700);
  }

  if (stage === 'done' && sale) {
    return <Receipt sale={sale} onNew={newSale} />;
  }

  if (stage === 'payment') {
    return (
      <Payment
        total={total} staff={staff} customer={customer}
        onBack={() => setStage('cart')} onComplete={complete} error={error} />
    );
  }

  return (
    <div style={{ display: 'flex', gap: 0, height: '100%', margin: -24 }}>
      {/* category rail */}
      <div className="sp-categories" style={{
        flex: 'none', background: 'var(--color-bg-surface)',
        borderRight: '1px solid var(--color-border-default)', padding: '12px 8px', overflowY: 'auto'
      }}>
        <RailRow label="All services" count={cat.services.length} on={!activeCat} onClick={() => setActiveCat(null)} />
        {cat.categories.map(c => (
          <RailRow key={c.id} label={c.name} count={c.count} colour={c.colour}
            on={activeCat === c.id} onClick={() => setActiveCat(c.id)} />
        ))}
      </div>

      {/* picker */}
      <div style={{ flex: 1, padding: 16, overflowY: 'auto', minWidth: 0 }}>
        <input
          value={query} onChange={e => setQuery(e.target.value)}
          placeholder={`Search ${cat.services.length} services`}
          style={{
            width: '100%', height: 48, padding: '0 16px', marginBottom: 16,
            background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)',
            borderRadius: 'var(--radius-md)', font: '400 15px/22px var(--font-ui)', color: 'var(--color-text-primary)'
          }} />
        <div className="tile-grid">
          {services.map(s => {
            const c = catById[s.category_id];
            return (
              <button key={s.id} onClick={() => addService(s)} className="tile"
                data-testid="service-tile" data-name={s.name}
                style={{ '--cat': c?.colour || 'var(--color-accent-default)' }}>
                <span className="cat">{c?.name}</span>
                {/* The full name, not an ellipsis after twelve characters */}
                <span className="name">{s.name}</span>
                <span className="foot">
                  <span className="money t-money-m" style={{ flex: 1 }}>
                    {s.price == null ? 'On request' : ghs(s.price)}
                  </span>
                  <span className="t-body-s muted">{minutes(s.duration_min)}</span>
                </span>
              </button>
            );
          })}
          {!services.length && <div className="t-body-m muted">Nothing matches that.</div>}
        </div>
      </div>

      {/* cart */}
      <div className="sp-cart" style={{
        flex: 'none', background: 'var(--color-bg-surface)',
        borderLeft: '1px solid var(--color-border-default)', display: 'flex', flexDirection: 'column'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 16 }}>
          <span className="t-heading-m">Cart</span>
          <span style={{ flex: 1 }} />
          <span className="t-body-s muted">{lines.length} item{lines.length === 1 ? '' : 's'}</span>
        </div>
        <Divider />
        <CustomerRow customer={customer} slug={slug} onPick={setCustomer} />
        <Divider />

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {!lines.length && (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <div className="t-heading-m secondary" style={{ marginBottom: 6 }}>Cart is empty</div>
              <div className="t-body-m muted">Choose a service to start. A walk-in needs no record at all.</div>
            </div>
          )}
          {lines.map(l => (
            <div key={l.key}>
              <button onClick={() => setEditing(l)}
                data-testid="cart-line" data-name={l.name}
                style={{
                  width: '100%', textAlign: 'left', border: 0, background: 'transparent',
                  padding: '12px 16px', cursor: 'pointer', display: 'grid', gap: 10, font: 'inherit', color: 'inherit'
                }}>
                <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <span className="t-body-m" style={{ flex: 1 }}>{l.name}</span>
                  <span className="money t-money-m">{ghs(Math.max(0, l.unitPrice * l.qty - l.discount))}</span>
                </span>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {/* A service line with no staff cannot pay commission, so the gap is loud.
                      Where the business credits nobody there is no gap and no chip. */}
                  {creditsStaff && (
                    <Chip tone={l.staffId ? 'accent' : 'danger'} size="m">
                      {l.staffName || 'Assign staff'}
                    </Chip>
                  )}
                  <Chip tone="neutral" showDot={false}>x{l.qty}</Chip>
                  {l.discount > 0 && <Chip tone="success" showDot={false}>{ghs(-l.discount)}</Chip>}
                </span>
              </button>
              <Divider />
            </div>
          ))}
        </div>

        {unassigned > 0 && (
          <div style={{ padding: '10px 16px', background: 'var(--color-status-danger-bg)', color: 'var(--color-status-danger)' }}
               className="t-body-s">
            {unassigned} line{unassigned === 1 ? '' : 's'} without a staff member. Commission cannot be paid on {unassigned === 1 ? 'it' : 'them'}.
          </div>
        )}

        <div style={{ background: 'var(--color-bg-subtle)', padding: 16, display: 'grid', gap: 8 }}>
          <Row label="Subtotal" value={ghs(subtotal)} />
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span className="t-heading-m" style={{ flex: 1 }}>Total</span>
            <span className="money t-money-xl">{ghs(total)}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: '12px 16px 16px' }}>
          <Button variant="secondary" onClick={hold} disabled={!lines.length}>{held ? 'Held' : 'Hold'}</Button>
          <Button style={{ flex: 1 }} disabled={!lines.length} onClick={() => setStage('payment')}>
            Pay {ghs(total)}
          </Button>
        </div>
      </div>

      {editing && (
        <LineEditor
          line={editing} staff={staff} slug={slug} creditsStaff={creditsStaff}
          onClose={() => setEditing(null)}
          onRemove={() => { setLines(ls => ls.filter(x => x.key !== editing.key)); setEditing(null); }}
          onSave={next => { setLines(ls => ls.map(x => x.key === next.key ? next : x)); setEditing(null); }} />
      )}
    </div>
  );
}

function RailRow({ label, count, colour, on, onClick }) {
  return (
    <button onClick={onClick}
      style={{
        '--cat': colour || 'var(--color-text-muted)',
        display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44,
        padding: '10px 12px', border: 0, borderRadius: 'var(--radius-sm)', cursor: 'pointer', textAlign: 'left',
        background: on ? 'var(--color-accent-subtle)' : 'transparent',
        color: on ? 'var(--color-accent-strong)' : 'var(--color-text-secondary)',
        font: '500 15px/20px var(--font-ui)'
      }}>
      <span className="cat-dot" style={{ opacity: colour ? 1 : 0.35 }} />
      <span style={{ flex: 1 }}>{label}</span>
      <span className="t-body-s muted">{count}</span>
    </button>
  );
}

function CustomerRow({ customer, slug, onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px' }}>
        <Chip tone={customer ? 'accent' : 'neutral'} showDot={Boolean(customer)}>
          {customer ? customer.name : 'Walk-in'}
        </Chip>
        <span style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-m" onClick={() => setOpen(true)}
          style={{ color: 'var(--color-accent-strong)' }}>
          {customer ? 'Change' : 'Add customer'}
        </button>
      </div>
      {open && <CustomerPicker slug={slug} onClose={() => setOpen(false)}
        onPick={c => { onPick(c); setOpen(false); }} onClear={() => { onPick(null); setOpen(false); }} />}
    </>
  );
}

/**
 * Find a customer, or make one, without leaving the sale.
 *
 * Making one used to mean abandoning the cart, going to Customers, adding them, and coming
 * back to start again. Nobody does that with somebody waiting, so in practice every new
 * client was rung up as a walk-in and the record never happened. The typing is the same
 * either way; the difference is whether it can be done here.
 *
 * What is typed into the search box becomes the name, because by the time somebody decides
 * to add a customer they have already typed who it is.
 */
function CustomerPicker({ slug, onClose, onPick, onClear }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [making, setMaking] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { b.customers(q).then(d => setRows(d.customers)).catch(() => {}); }, [q]);

  // A phone number typed into the search box is a phone number, not a name.
  const looksLikePhone = /^[0-9+\s-]{6,}$/.test(q.trim());

  async function create(e) {
    e?.preventDefault();
    setSaving(true); setError(null);
    try {
      const { customer } = await b.createCustomer(making);
      onPick(customer);
    } catch (err) {
      setError(err.data?.error === 'name_required' ? 'A name is needed.' : 'That did not save.');
      setSaving(false);
    }
  }

  if (making) {
    return (
      <Sheet title="New customer" subtitle="A name is enough. Everything else can wait." onClose={() => setMaking(null)}>
        <form onSubmit={create} style={{ display: 'contents' }}>
          <Section label="DETAILS">
            <Field id="nc-name" label="NAME" value={making.name} autoFocus
              onChange={e => setMaking(m => ({ ...m, name: e.target.value }))} />
            <Field id="nc-phone" label="PHONE" value={making.phone}
              hint="Used to look them up at the counter next time"
              onChange={e => setMaking(m => ({ ...m, phone: e.target.value }))} />
            {error && <Banner tone="danger">{error}</Banner>}
          </Section>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', gap: 8, padding: '16px 24px 24px', background: 'var(--color-bg-subtle)' }}>
            <Button type="button" variant="ghost" onClick={() => setMaking(null)}>Back</Button>
            <Button type="submit" style={{ flex: 1 }} disabled={saving || !making.name.trim()}>
              {saving ? 'Saving' : 'Save and attach to this sale'}
            </Button>
          </div>
        </form>
      </Sheet>
    );
  }

  return (
    <Sheet title="Add customer" subtitle="Walk-ins need no record. Skip this and sell." onClose={onClose}>
      <div style={{ padding: '16px 24px' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by phone or name" autoFocus
          style={{
            width: '100%', height: 48, padding: '0 16px', background: 'var(--color-bg-surface)',
            border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-md)',
            font: '400 15px/22px var(--font-ui)', color: 'var(--color-text-primary)'
          }} />
      </div>
      <Divider />
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {rows.map(c => (
          <div key={c.id}>
            <button onClick={() => onPick(c)}
              style={{ width: '100%', textAlign: 'left', border: 0, background: 'transparent', padding: '12px 24px', cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'center', font: 'inherit', color: 'inherit' }}>
              <span style={{ flex: 1 }}>
                <span className="t-label-l" style={{ display: 'block' }}>{c.name}</span>
                <span className="t-body-s muted">{c.phone} · {ghs(c.total_spend)} lifetime</span>
              </span>
              {c.balance > 0 && <Chip tone="danger">Owes {ghs(c.balance)}</Chip>}
            </button>
            <Divider />
          </div>
        ))}
      </div>
      {q.trim() && !rows.length && (
        <div style={{ padding: '16px 24px' }} className="t-body-s muted">
          Nobody by that. New customer below puts them on the books and on this sale.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, padding: '16px 24px 24px', background: 'var(--color-bg-subtle)' }}>
        <Button variant="ghost" onClick={onClear}>Walk-in, no record</Button>
        <span style={{ flex: 1 }} />
        <Button onClick={() => {
          const typed = q.trim();
          setMaking(looksLikePhone ? { name: '', phone: typed } : { name: typed, phone: '' });
        }}>New customer</Button>
      </div>
    </Sheet>
  );
}

function LineEditor({ line, staff, slug, creditsStaff = true, onClose, onSave, onRemove }) {
  const [draft, setDraft] = useState(line);
  const [able, setAble] = useState(null);
  const b = useMemo(() => api.b(slug), [slug]);

  // Only the people whose speciality covers this service are offered.
  useEffect(() => {
    if (!creditsStaff) return;
    if (!line.serviceId) { setAble(staff); return; }
    b.staff(line.serviceId).then(d => setAble(d.staff)).catch(() => setAble(staff));
  }, [line.serviceId, creditsStaff]);

  // The specialists are what the picker offers by default, because that is right almost
  // every time and a list of everybody is a list to hunt through. But almost every time is
  // not every time: somebody covers a colleague, somebody learns a new service, somebody is
  // simply the one who did it. So the rest of the team is one tap away rather than absent,
  // and it is still the team, never a name typed into a box that nothing can pay out to.
  const [showEveryone, setShowEveryone] = useState(false);
  const options = showEveryone ? staff : (able ?? []);
  const net = Math.max(0, draft.unitPrice * draft.qty - draft.discount);
  const chosen = options.find(s => s.id === draft.staffId);
  // Whether this person is on commission at all. Ezel Spa is salaried, so for them the
  // reason to assign a line is the record of who did the work, not a payout. Saying
  // "earns 0% of line net" would be true and useless.
  const onCommission = chosen && chosen.commission_type !== 'none' && Number(chosen.commission_value) > 0;
  const commission = onCommission ? Math.round((net * Number(chosen.commission_value)) / 100) : 0;
  const excluded = staff.length - (able ?? []).length;

  return (
    <Sheet title="Edit line" subtitle={line.name} onClose={onClose}>
      {creditsStaff && <>
      <Section label="ASSIGN STAFF">
        {!options.length && able && (
          <div className="t-body-m" style={{ color: 'var(--color-status-danger)' }}>
            Nobody is set up to perform this. Add the category to whoever does it on the Staff screen.
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {options.map(s => {
            const on = draft.staffId === s.id;
            return (
              <button key={s.id} onClick={() => setDraft(d => ({
                  ...d,
                  staffId: on ? null : s.id,
                  staffName: on ? null : s.name,
                  // The server refuses a line credited outside somebody's specialities unless
                  // the caller says it meant it. Tapping a name from Show everyone is exactly
                  // that, and it is recorded so a payout query can find it later.
                  outsideSpeciality: on ? false : !(able ?? []).some(x => x.id === s.id)
                }))}
                style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer' }}>
                <Chip tone={on ? 'accent' : 'neutral'} size="m" showDot={on}>{s.name}</Chip>
              </button>
            );
          })}
        </div>
        <div className="t-body-s muted" style={{ marginTop: 10 }}>
          {chosen && !(able ?? []).some(s => s.id === chosen.id)
            ? `${chosen.name} is credited with this line. It is not one of their specialities, which is fine, it just will not have been on their list.`
            : !chosen
            ? 'Nobody is credited with this line yet. Their name prints on the receipt and the work counts as theirs.'
            : onCommission
              ? `${chosen.name} earns ${chosen.commission_value}% of line net, ${ghs(commission)} on this line.`
              : `${chosen.name} is credited with this line. Their name prints on the receipt beside it.`}
        </div>
        {excluded > 0 && (
          <div className="t-body-s muted" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>
              {showEveryone
                ? `Showing the whole team. ${excluded} of them do not have this as a speciality.`
                : `${excluded} other${excluded === 1 ? '' : 's'} on the team, not set up for this.`}
            </span>
            <button onClick={() => setShowEveryone(v => !v)}
              style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', font: 'inherit' }}>
              <Chip tone={showEveryone ? 'accent' : 'neutral'} showDot={false}>
                {showEveryone ? 'Just the specialists' : 'Show everyone'}
              </Chip>
            </button>
          </div>
        )}
      </Section>
      <Divider />
      </>}

      <Section label="QUANTITY">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Stepper onClick={() => setDraft(d => ({ ...d, qty: Math.max(1, d.qty - 1) }))}>-</Stepper>
          <Stepper wide>{draft.qty}</Stepper>
          <Stepper onClick={() => setDraft(d => ({ ...d, qty: d.qty + 1 }))}>+</Stepper>
        </div>
      </Section>
      <Divider />
      <Section label="PRICE AND DISCOUNT">
        <MoneyInput label="Unit price" value={draft.unitPrice} onChange={v => setDraft(d => ({ ...d, unitPrice: v }))} />
        <MoneyInput label="Line discount" value={draft.discount} onChange={v => setDraft(d => ({ ...d, discount: v }))} tone="success" />
        <div className="t-body-s muted">Line net {ghs(net)}. A discount comes out of the stylist's share too.</div>
      </Section>
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', gap: 8, padding: '16px 24px 24px', background: 'var(--color-bg-subtle)' }}>
        <Button variant="danger" onClick={onRemove}>Remove</Button>
        <Button style={{ flex: 1 }} onClick={() => onSave(draft)}>Save line</Button>
      </div>
    </Sheet>
  );
}

function MoneyInput({ label, value, onChange, tone }) {
  const [text, setText] = useState((value / 100).toFixed(2));
  useEffect(() => { setText((value / 100).toFixed(2)); }, [value]);
  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 12, height: 48, padding: '0 16px',
      background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)',
      borderRadius: 'var(--radius-md)'
    }}>
      <span className="t-body-m secondary" style={{ flex: 1 }}>{label}</span>
      <span className="t-body-s muted">GHS</span>
      <input
        className="money" value={text}
        onChange={e => setText(e.target.value)}
        onBlur={() => onChange(toPesewas(text))}
        inputMode="decimal"
        style={{
          width: 110, textAlign: 'right', border: 0, background: 'transparent', fontSize: 18,
          color: tone === 'success' ? 'var(--color-status-success)' : 'var(--color-text-primary)'
        }} />
    </label>
  );
}

const Stepper = ({ children, onClick, wide }) => (
  <button onClick={onClick} disabled={!onClick}
    style={{
      width: wide ? 72 : 56, height: 56, borderRadius: 'var(--radius-md)',
      border: '1px solid var(--color-border-default)',
      background: wide ? 'var(--color-bg-surface)' : 'var(--color-bg-subtle)',
      cursor: onClick ? 'pointer' : 'default', font: '500 24px/32px var(--font-money)',
      color: 'var(--color-text-primary)'
    }}>{children}</button>
);

export function Sheet({ title, subtitle, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', zIndex: 40 }}>
      <div onClick={onClose} style={{ flex: 1, background: 'rgba(2,6,23,0.45)' }} />
      <div style={{
        width: 480, background: 'var(--color-bg-surface)', display: 'flex', flexDirection: 'column',
        boxShadow: 'var(--elevation-lg)'
      }}>
        <div style={{ padding: '20px 24px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="t-heading-l" style={{ flex: 1 }}>{title}</span>
            <button className="btn btn-ghost btn-m" onClick={onClose}>Close</button>
          </div>
          {subtitle && <div className="t-body-m secondary" style={{ marginTop: 4 }}>{subtitle}</div>}
        </div>
        <Divider />
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>{children}</div>
      </div>
    </div>
  );
}

export const Divider = () => <div style={{ height: 1, background: 'var(--color-border-default)', flex: 'none' }} />;

export function Section({ label, children }) {
  return (
    <div style={{ padding: '16px 24px', display: 'grid', gap: 10 }}>
      <div className="t-label-xs muted">{label}</div>
      {children}
    </div>
  );
}

export function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span className="t-body-m secondary" style={{ flex: 1 }}>{label}</span>
      <span className="money t-money-m secondary">{value}</span>
    </div>
  );
}
